import { useState, useRef, useEffect } from 'react';
import { energy, msgOpacity, hexAlpha } from '../lib/energy';

export interface TileMessage {
  id: string;
  text: string;
  submittedAt: number;
}

interface TileProps {
  name: string;
  color: string;
  buffer: string;
  messages: TileMessage[];
  lastActivity: number | null;
  isUser: boolean;
  now: number;
  onNameChange?: (name: string) => void;
}

export function Tile({ name, color, buffer, messages, lastActivity, isUser, now, onNameChange }: TileProps) {
  const rawEnergy = energy(lastActivity, now);
  // User's own tile never fades - always visibly present
  const en = isUser ? Math.max(0.4, rawEnergy) : rawEnergy;
  const g = en * 26;
  const isTyping = buffer.length > 0;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) nameInputRef.current?.focus();
  }, [editing]);

  const commitName = () => {
    const trimmed = draft.trim().slice(0, 32);
    setEditing(false);
    if (trimmed && trimmed !== name && onNameChange) {
      onNameChange(trimmed);
    } else {
      setDraft(name);
    }
  };

  // Tile collapses to compact header when no visible content (not for user's tile)
  const hasVisibleMessages = messages.some(m => msgOpacity(m.submittedAt, now) > 0);
  const hasContent = isUser || buffer.length > 0 || hasVisibleMessages;

  return (
    <div
      className={hasContent ? 'pulse-tile' : 'pulse-tile pulse-tile-compact'}
      style={{
        borderRadius: hasContent ? 14 : 10,
        border: `1px solid ${hexAlpha(color, en * 100)}`,
        padding: hasContent ? '18px 20px 10px' : '10px 14px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        transition:
          'opacity 1s cubic-bezier(.4,0,.2,1), box-shadow 1.2s ease, background 1s ease, border-color 0.8s ease, padding 0.4s ease, min-height 0.4s ease, border-radius 0.3s ease',
        // Tile is always visible while connected - energy only drives glow, not existence
        opacity: Math.max(0.55, en * 0.45 + 0.55),
        boxShadow:
          en > 0.2
            ? `0 0 ${g}px ${hexAlpha(color, en * 55)}, inset 0 0 ${g * 0.35}px ${hexAlpha(color, en * 25)}`
            : 'none',
        background: `linear-gradient(170deg, rgba(${en > 0.35 ? '32,26,18' : '13,13,20'}, ${0.5 + en * 0.3}), rgba(10,10,16, ${0.6 + en * 0.2}))`,
        cursor: isUser ? 'text' : 'default',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: hasContent ? 14 : 0 }}>
        <div
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            backgroundColor: color,
            boxShadow: en > 0.35 ? `0 0 10px ${hexAlpha(color, 140)}` : 'none',
            transition: 'all 0.7s',
          }}
        />
        {isUser && editing ? (
          <input
            ref={nameInputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') commitName();
              if (e.key === 'Escape') { setDraft(name); setEditing(false); }
            }}
            onClick={(e) => e.stopPropagation()}
            maxLength={32}
            style={{
              fontFamily: '"Space Mono", monospace',
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 1.8,
              textTransform: 'lowercase',
              color,
              background: 'rgba(255,255,255,.06)',
              border: `1px solid ${hexAlpha(color, 80)}`,
              borderRadius: 4,
              padding: '2px 6px',
              outline: 'none',
              width: 140,
            }}
          />
        ) : (
          <span
            onClick={(e) => {
              if (!isUser || !onNameChange) return;
              e.stopPropagation();
              setDraft(name);
              setEditing(true);
            }}
            style={{
              fontFamily: '"Space Mono", monospace',
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 1.8,
              textTransform: 'lowercase',
              color,
              transition: 'color 0.7s',
              cursor: isUser && onNameChange ? 'pointer' : 'default',
              borderBottom: isUser && onNameChange ? '1px dashed rgba(255,255,255,.12)' : 'none',
            }}
          >
            {name}
          </span>
        )}
      </div>

      {/* Messages + buffer (hidden when compact) */}
      {hasContent && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minHeight: 50 }}>
          {messages.map((m) => {
            const op = msgOpacity(m.submittedAt, now);
            if (op <= 0) return null;
            return (
              <div key={m.id} style={{ opacity: op, transition: 'opacity 0.8s ease', lineHeight: 1.6 }}>
                <span
                  style={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: 14,
                    color: '#d5d1c8',
                    wordBreak: 'break-word',
                    letterSpacing: 0.3,
                  }}
                >
                  {m.text}
                </span>
              </div>
            );
          })}

          {/* Current buffer + cursor */}
          {(buffer || isUser) ? (
            <div style={{ lineHeight: 1.6 }}>
              {buffer && (
                <span
                  style={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: 14,
                    color: '#d5d1c8',
                    letterSpacing: 0.3,
                    wordBreak: 'break-word',
                  }}
                >
                  {buffer}
                </span>
              )}
              {isUser && (
                <span
                  style={{
                    display: 'inline-block',
                    width: 2.5,
                    height: 17,
                    marginLeft: 1,
                    backgroundColor: color,
                    animation: 'blink .75s ease-in-out infinite',
                    verticalAlign: 'text-bottom',
                    borderRadius: 1,
                  }}
                />
              )}
              {!isUser && buffer && (
                <span
                  style={{
                    display: 'inline-block',
                    width: 2.5,
                    height: 17,
                    marginLeft: 1,
                    backgroundColor: color,
                    animation: 'blink .75s ease-in-out infinite',
                    verticalAlign: 'text-bottom',
                    borderRadius: 1,
                  }}
                />
              )}
            </div>
          ) : null}
        </div>
      )}

    </div>
  );
}
